// Capacitor platform bridge. Uses the global Capacitor object when the
// native shell injects it — no hard npm import, so web localhost never
// blanks if @capacitor/* packages are not installed yet.

import {
    isRevenueCatKeyConfigured,
    isRewardedAdsConfigured,
} from "./revenueCatConfig";

export const NATIVE_PLATFORMS = {
    WEB: "web",
    IOS: "ios",
    ANDROID: "android",
};

/**
 * Capacitor global when present (native WebView or after core loads).
 * Avoids a static `@capacitor/core` import on the web critical path.
 */
export function getCapacitor() {
    try {
        if (typeof window !== "undefined" && window.Capacitor) {
            return window.Capacitor;
        }
    } catch (_) {
        // ignore
    }
    return null;
}

/**
 * True when running inside a Capacitor native shell (not the browser).
 */
export function isNativeApp() {
    const cap = getCapacitor();
    try {
        return Boolean(
            cap &&
                typeof cap.isNativePlatform === "function" &&
                cap.isNativePlatform()
        );
    } catch (_) {
        return false;
    }
}

/**
 * @returns {"web"|"ios"|"android"|string}
 */
export function getNativePlatform() {
    const cap = getCapacitor();
    try {
        if (cap && typeof cap.getPlatform === "function") {
            return cap.getPlatform();
        }
    } catch (_) {
        // ignore
    }
    return NATIVE_PLATFORMS.WEB;
}

export function isIosApp() {
    return getNativePlatform() === NATIVE_PLATFORMS.IOS;
}

export function isAndroidApp() {
    return getNativePlatform() === NATIVE_PLATFORMS.ANDROID;
}

/**
 * RevenueCat Purchases plugin when Capacitor registered it (native shell).
 * Web has no plugin unless a native WebView injected it.
 */
export function getPurchasesPlugin() {
    const cap = getCapacitor();
    try {
        if (cap && cap.Plugins && cap.Plugins.Purchases) {
            return cap.Plugins.Purchases;
        }
    } catch (_) {
        // ignore
    }
    return null;
}

/**
 * True in a native shell when RevenueCat public keys are pasted over
 * "Replace later" and the Purchases plugin is present.
 */
export function isNativeBillingReady() {
    if (!isNativeApp()) {
        return false;
    }
    if (!isRevenueCatKeyConfigured(getNativePlatform())) {
        return false;
    }
    return Boolean(getPurchasesPlugin());
}

/**
 * Rewarded ads SDK is not wired yet. Heart funnel uses the preview grant
 * until AdMob unit ids replace "Replace later" and an ads plugin ships.
 */
export function isNativeAdsReady() {
    if (!isNativeApp() || !isRewardedAdsConfigured()) {
        return false;
    }
    const cap = getCapacitor();
    try {
        return Boolean(cap && cap.Plugins && cap.Plugins.AdMob);
    } catch (_) {
        return false;
    }
}
